import { useEffect, useState } from 'react';
import { Award, ArrowRight, ShieldCheck, BadgeCheck, Headphones, GraduationCap } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Partners() {
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    axios.get(`${API}/partners`).then((r) => setPartners(r.data)).catch(() => {});
  }, []);

  const microsoftBenefits = [
    { icon: ShieldCheck, text: 'Directe toegang tot enterprise technologie' },
    { icon: BadgeCheck, text: 'Gecertificeerde implementaties' },
    { icon: Headphones, text: 'Prioriteit support kanalen' },
    { icon: GraduationCap, text: 'Uitgebreide training programma\'s' },
  ];

  return (
    <div data-testid="partners-page">
      <section className="relative pt-40 pb-24 overflow-hidden" style={{ background: 'linear-gradient(135deg, #16224b 0%, #233a80 58%, #f2a100 100%)' }}>
        <div className="dot-pattern absolute inset-0" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <h1 className="text-white font-bold tracking-tight mb-4" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', letterSpacing: '-0.03em' }}>
            Onze Partners
          </h1>
          <p className="text-white/70 max-w-xl mx-auto text-base leading-8">
            Wij werken samen met toonaangevende technologiepartners om u de beste
            oplossingen te bieden. Als gecertificeerd partner garanderen wij expertise en kwaliteit.
          </p>
          <div className="mt-8">
            <Link to="/contact" className="btn-gradient">
              Word Partner <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="admin-surface p-8 lg:p-10 grid lg:grid-cols-[1fr_280px] gap-10 items-center">
            <div>
              <h2 className="text-[#16224b] font-bold mb-4" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.2rem)' }}>
                Microsoft Partner
              </h2>
              <p className="text-[#6b7184] leading-8 mb-6">
                Als Microsoft Partner beschikken wij over de juiste certificeringen en directe
                toegang tot Microsoft resources. Dit garandeert u de beste implementaties en support.
              </p>

              <div className="grid sm:grid-cols-2 gap-3">
                {microsoftBenefits.map((item) => (
                  <div key={item.text} className="flex items-center gap-3 text-sm text-[#16224b] bg-[#f8f9fd] border border-gray-100 rounded-lg px-4 py-3">
                    <item.icon className="w-4 h-4 text-[#3d56a6]" />
                    {item.text}
                  </div>
                ))}
              </div>

              <Link to="/diensten/microsoft365" className="btn-outline-dark mt-7">
                Microsoft 365 Diensten <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="rounded-lg border border-gray-100 bg-white p-8 flex items-center justify-center">
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/9/96/Microsoft_logo_%282012%29.svg"
                alt="Microsoft"
                className="w-full max-w-[240px] object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#f8f9fd]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="text-[#16224b] font-bold" style={{ fontSize: 'clamp(1.75rem, 3vw, 2.4rem)' }}>
              Onze Technologie Partners
            </h2>
          </div>

          {partners.length === 0 ? (
            <div className="text-center py-20">
              <Award className="w-16 h-16 text-gray-200 mx-auto mb-4" />
              <p className="text-[#6b7184]">Partners worden binnenkort toegevoegd.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
              {partners.map((partner, i) => (
                <a
                  key={partner.id || i}
                  href={partner.website || '#'}
                  target={partner.website ? '_blank' : undefined}
                  rel={partner.website ? 'noreferrer' : undefined}
                  className="bg-white p-8 rounded-lg border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex items-start gap-6"
                >
                  {partner.logo ? (
                    <div className="w-20 h-20 rounded-xl bg-white border border-gray-100 flex-shrink-0 flex items-center justify-center overflow-hidden p-3">
                      <img src={partner.logo} alt={partner.naam} className="w-full h-full object-contain" />
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-xl flex-shrink-0 flex items-center justify-center font-bold text-white text-lg bg-[#3d56a6]">
                      {partner.naam?.charAt(0)}
                    </div>
                  )}
                  <div>
                    <h3 className="text-[#16224b] font-bold mb-2">{partner.naam}</h3>
                    <p className="text-[#6b7184] text-sm leading-7">{partner.beschrijving}</p>
                    <span className="inline-flex items-center gap-1 text-[#3d56a6] text-xs font-semibold mt-3">
                      Bezoek website <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-20 bg-white text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-[#16224b] font-bold mb-4" style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
            Interesse om partner te worden?
          </h2>
          <p className="text-[#6b7184] mb-8">
            Neem contact op om de mogelijkheden te bespreken.
          </p>
          <Link to="/contact" className="btn-gradient text-base px-10 py-4">
            Neem Contact Op <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
